import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RestaurantModel } from '@/features/auth/api/AuthorizationAPI.model'
import { useCurrentAccount } from '@/features/auth/hooks/useCurrentAccount'
import { switchRestaurant } from '@/features/auth/services/currentRestaurant'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import Select from '@/shared/components/Select/Select'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { reopenAfterReload } from '../store/useSettingsModal'
import styles from '../SettingsModal.module.scss'

interface RestaurantSwitcherProps {
  /** Edits in the section not saved yet, which a switch would throw away. */
  hasUnsaved: boolean
}

/**
 * Which venue Settings > Restaurant is about, and the way to the others: the
 * account's venues in a select once there is more than one. A pick switches
 * the whole app to that venue, which reloads the page, and opens this section
 * again on it.
 */
function RestaurantSwitcher({ hasUnsaved }: RestaurantSwitcherProps) {
  const { t } = useTranslation()
  const { restaurants, restaurantId } = useCurrentAccount()
  const isPageDirty = useFormDirtyStore(s => s.isDirty)
  const [pending, setPending] = useState<RestaurantModel | null>(null)
  /*
    A pick never changes the venue in place: it reloads or waits on a
    question. The field must go on naming the open one meanwhile, and MUI
    keeps the picked name in it while the value it is given stays the same,
    so it is started again on every pick.
  */
  const [pickCount, setPickCount] = useState(0)

  const options: OptionType[] = restaurants.map(venue => ({
    value: String(venue.id),
    label: venue.name
  }))
  const current = options.find(option => option.value === restaurantId) ?? null

  // A list of one would be a choice of one.
  if (options.length < 2) return null

  const open = (venue: RestaurantModel) => {
    reopenAfterReload('restaurant')
    switchRestaurant(venue)
  }

  /*
    The reload drops what is not saved, in this section or on the page under
    the dialog, and the router's guard never sees a reload. So either is asked
    about first: in the page's words when the page has edits, which covers
    both, and in this section's when only it has.
  */
  const pick = (value: selectValueType) => {
    setPickCount(count => count + 1)
    const picked = (value as OptionType | null)?.value
    const venue = restaurants.find(entry => String(entry.id) === picked)
    if (!venue || picked === restaurantId) return
    if (hasUnsaved || isPageDirty) setPending(venue)
    else open(venue)
  }

  return (
    <div className={styles.switcher}>
      <Select
        key={pickCount}
        label={t('SETTINGS.RESTAURANT.SWITCH')}
        options={options}
        value={current}
        onChange={pick}
        hideClearIcon
        keepCase
        focused
        flexGrow={1}
      />
      <ConfirmationModal
        isOpen={pending !== null}
        title={t(
          isPageDirty ? 'LOCATION_CHANGE_MODAL.TITLE' : 'SETTINGS.UNSAVED_TITLE'
        )}
        message={t(
          isPageDirty
            ? 'LOCATION_CHANGE_MODAL.DESCRIPTION'
            : 'SETTINGS.UNSAVED_MESSAGE'
        )}
        onClose={() => setPending(null)}
        onConfirm={() => {
          if (pending) open(pending)
        }}
        // Opened from inside the settings modal (Modal.tsx).
        disablePortal={false}
      />
    </div>
  )
}

export default RestaurantSwitcher
